import React from 'react'
import './ExpandingOptionSelector.scss'
import classNames from 'classnames'
import ReactResizeDetector from 'react-resize-detector'
import Option, { KeyType } from '../utils/Option'

interface Props<KEY extends KeyType> {
  label: string
  options: readonly Option<KEY>[]
  selected: KEY | null
  selectedCollapsedLabel?: string
  setSelected: (option: KEY) => void
  onExpand: () => void
  expanded: boolean
  icon?: string
  colorStyle?: 'green' | 'grey'
}

export const ExpandingOptionSelectorClassName = 'ExpandingOptionSelector'

const SelectedItemScrollDebounceDelay = 200

function ExpandingOptionSelectorItem(props: {
  entryDelay: number
  onClick: () => void
  label: string
  selected: boolean
  expanded: boolean
}) {
  const { selected, expanded, entryDelay, onClick, label } = props

  const ref = React.useRef<HTMLSpanElement | null>(null)

  React.useLayoutEffect(() => {
    if (selected && expanded) {
      // Scroll into view after debounce delay
      const timer = setTimeout(() => {
        ref.current?.scrollIntoView({
          block: 'center',
          inline: 'center',
          behavior: 'smooth',
        })
      }, SelectedItemScrollDebounceDelay)

      // Cancel timeout if selection changes
      return () => {
        window.clearTimeout(timer)
      }
    }
  }, [selected, expanded])

  return (
    <span
      ref={ref}
      className={classNames('option', { selected })}
      style={{
        animationDelay: `${entryDelay}ms`,
      }}
      onClick={onClick}
    >
      <div>{label}</div>
    </span>
  )
}

function ExpandingOptionSelector<KEY extends KeyType = KeyType>(props: Props<KEY>) {
  const { label, options, selected, setSelected, expanded, onExpand, icon, selectedCollapsedLabel } = props

  const colorStyle: 'green' | 'grey' = props.colorStyle ?? 'green'

  const selectedLabel = selectedCollapsedLabel ?? options.find(it => it.key === selected)?.label ?? selected

  const [labelWidth, setLabelWidth] = React.useState<number|undefined>()
  const [optionsWidth, setOptionsWidth] = React.useState<number|undefined>()

  const size = expanded ? optionsWidth : labelWidth

  return (
    <div
      className={classNames(ExpandingOptionSelectorClassName, colorStyle, { expanded })}
      onClick={() => { if (!expanded) { onExpand() } }}
    >
      {
        icon ? (
          <div
            className="icon"
            style={{
              WebkitMaskImage: `url(${icon})`,
              maskImage: `url(${icon})`,
            }}
          />
        ) : (
          <span
            className="icon"
          />
        )
      }
      <div
        className="expandingSection"
        style={{ width: size !== undefined ? `${size}px` : 'auto' }}
      >
        {
          <span className="label">
            <ReactResizeDetector onResize={width => setLabelWidth(width)} handleWidth />
            <div className="text">
              {label}: <strong>{selectedLabel}</strong>
            </div>
          </span>
        }
        {
          <div className="options">
            <ReactResizeDetector onResize={width => setOptionsWidth(width)} handleWidth />
            {
              options.map((option, index) => (
                <ExpandingOptionSelectorItem
                  key={option.key}
                  selected={selected === option.key}
                  onClick={() => setSelected(option.key)}
                  label={option.label}
                  entryDelay={index * 100}
                  expanded={expanded}
                />
              ))
            }
          </div>
        }
      </div>
    </div>
  )
}

export default ExpandingOptionSelector
